/**
 * Pairing Page ViewModel
 *
 * STRICT MVVM PATTERN - ALL business logic in ViewModel
 * Returns: { state, actions, computed, onMount }
 *
 * Handles immediate pairing flow:
 * - Immediate: User enters 2-word code → pair immediately
 */

import { useState } from "react";
import { useParams, NavigateFunction } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  pairImmediatelyAsync,
  unpairItemAsync,
  getScanContextAsync,
  selectPairing,
  setNfcUrl,
  resetPairing,
} from "@/store/slices/pairingSlice";
import { selectIsAuthenticated, setReturnUrl } from "@/store/slices/authSlice";
import { nfcUrlParser, NfcUrlParserError } from "@/utils/nfcUrlParser";
import type {
  PairingCode,
  ProductInfo,
  OwnerNfcScanContextResponse,
} from "@/domain/ports/IPairingGateway";

/**
 * ViewModel State
 */
export interface PairingViewModelState {
  /** Current pairing flow status */
  status: "idle" | "parsing" | "ready" | "pairing" | "success" | "error";
  /** Parsed NFC URL */
  nfcUrl: string | null;
  /** Product information (once parsed/fetched) */
  product: ProductInfo | null;
  /** Loading state for async operations */
  loading: boolean;
  /** Error information */
  error: {
    code: string;
    message: string;
  } | null;
  /** First word of pairing code */
  codeFirst: string;
  /** Second word of pairing code */
  codeSecond: string;
  /** Sequential item number from NFC URL (e.g., 1 in "1/100") */
  itemNumber?: number;
  /** Scan context (item status and routing information) */
  scanContext: OwnerNfcScanContextResponse | null;
  /** Scan context loading state */
  scanContextLoading: boolean;
  /** Scan context error */
  scanContextError: string | null;
}

/**
 * ViewModel Actions
 */
export interface PairingViewModelActions {
  /** Set first word of code */
  setCodeFirst: (value: string) => void;
  /** Set second word of code */
  setCodeSecond: (value: string) => void;
  /** Pair immediately with 2-word code */
  pairImmediately: () => Promise<void>;
  /** Unpair item from Trophy Room (calls future endpoint from Story 3.6.b) */
  unpairItem: () => Promise<void>;
  /** Navigate to collection page */
  goToCollection: () => void;
  /** Reset pairing state */
  reset: () => void;
}

/**
 * ViewModel Computed Values (Simplified - routing handled by NfcScanPage)
 */
export interface PairingViewModelComputed {
  /** Can pair immediately (both code words entered) */
  canPairImmediately: boolean;
  /** Product display info */
  productInfo: {
    title: string;
    imageUrl: string;
  } | null;
}

/**
 * ViewModel Return Type
 */
export interface PairingViewModel {
  state: PairingViewModelState;
  actions: PairingViewModelActions;
  computed: PairingViewModelComputed;
  onMount: () => () => void;
}

/**
 * usePairingViewModel Hook
 *
 * All business logic for Pairing page
 * React Compiler automatically memoizes functions
 *
 * @param navigate - Navigation function from useNavigate() (injected for navigation logic)
 */
export function usePairingViewModel(
  navigate: NavigateFunction
): PairingViewModel {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams<{
    productId: string;
    itemId: string;
    signature: string;
  }>();
  const pairingState = useAppSelector(selectPairing);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Local state for code inputs
  const [codeFirst, setCodeFirstState] = useState("");
  const [codeSecond, setCodeSecondState] = useState("");

  // State
  const state: PairingViewModelState = {
    ...pairingState,
    codeFirst,
    codeSecond,
    scanContext: pairingState.scanContext,
    scanContextLoading: pairingState.scanContextLoading,
    scanContextError: pairingState.scanContextError,
  };

  // Computed (Simplified - only pairing-related logic)
  const computed: PairingViewModelComputed = {
    canPairImmediately: codeFirst.trim() !== "" && codeSecond.trim() !== "",
    productInfo: pairingState.product
      ? {
          title: pairingState.product.title,
          imageUrl: pairingState.product.imageUrl,
        }
      : null,
  };

  // Actions
  const actions: PairingViewModelActions = {
    setCodeFirst: (value: string) => {
      setCodeFirstState(value.toLowerCase().trim());
    },

    setCodeSecond: (value: string) => {
      setCodeSecondState(value.toLowerCase().trim());
    },

    pairImmediately: async () => {
      if (!computed.canPairImmediately) {
        toast.error(t("pairing.errors.invalidCode"));
        return;
      }

      if (!pairingState.nfcUrl) {
        toast.error(t("pairing.errors.parseError"));
        return;
      }

      const code: PairingCode = {
        first: codeFirst,
        second: codeSecond,
      };

      try {
        await dispatch(
          pairImmediatelyAsync({
            nfcUrl: pairingState.nfcUrl,
            code,
          })
        ).unwrap();

        // Success handled by Redux state change → page will render success view
      } catch (error: any) {
        // Error codes from PairingError
        const errorCode = error.code || "UnknownError";
        const errorKey = `pairing.errors.${
          errorCode.charAt(0).toLowerCase() + errorCode.slice(1)
        }`;
        toast.error(
          t(errorKey, {
            defaultValue: error.message || t("pairing.errors.networkError"),
          })
        );
      }
    },

    unpairItem: async () => {
      if (!pairingState.linkedNfcId) {
        toast.error(t("pairing.errors.noLinkedItem"));
        return;
      }

      try {
        await dispatch(
          unpairItemAsync({
            itemId: pairingState.linkedNfcId,
          })
        ).unwrap();

        toast.success(t("pairing.unpair.success"));
        // After unpair, navigate to collection
        navigate("/owner/trophy-room");
      } catch (error: any) {
        const errorCode = error.code || "UnknownError";
        const errorKey = `pairing.errors.${
          errorCode.charAt(0).toLowerCase() + errorCode.slice(1)
        }`;
        toast.error(
          t(errorKey, {
            defaultValue: error.message || t("pairing.errors.networkError"),
          })
        );
      }
    },

    goToCollection: () => {
      navigate("/owner/trophy-room");
    },

    reset: () => {
      dispatch(resetPairing());
      setCodeFirstState("");
      setCodeSecondState("");
    },
  };

  // Lifecycle
  const onMount = () => {
    // Parse NFC URL from route params
    const { productId, itemId, signature } = params;

    if (!productId || !itemId || !signature) {
      toast.error(t("pairing.errors.parseError"));
      navigate("/404");
      return () => {};
    }

    // Reconstruct full NFC URL using env var for domain
    const nfcBaseUrl = import.meta.env.VITE_NFC_BASE_URL || 'https://app.anyw3ar.com';
    const nfcUrl = `${nfcBaseUrl}/nfc/${productId}/${itemId}/${signature}`;

    try {
      // Validate URL format
      nfcUrlParser.parse(nfcUrl);

      // Set NFC URL in Redux (triggers 'ready' status)
      dispatch(setNfcUrl(nfcUrl));

      // Check authentication before fetching scan context
      if (!isAuthenticated) {
        // Store the current route path (not the full nfcUrl) for post-login redirect
        const currentPath = `/nfc/${productId}/${itemId}/${signature}`;
        dispatch(setReturnUrl(currentPath));
        // Redirect to login page
        navigate("/auth/login");
        return () => {};
      }

      // Fetch scan context to determine routing (only if authenticated)
      dispatch(getScanContextAsync(nfcUrl));
    } catch (error) {
      if (error instanceof NfcUrlParserError) {
        toast.error(t("pairing.errors.parseError"));
        navigate("/404");
      } else {
        toast.error(t("pairing.errors.networkError"));
      }
    }

    // Cleanup on unmount
    return () => {
      // Reset pairing state when leaving page
      dispatch(resetPairing());
    };
  };

  // Navigation logic removed - handled by NfcScanPage router now

  return { state, actions, computed, onMount };
}
